import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Pencil } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Switch } from '@/app/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePayrollSettings } from '@/hooks/use-payroll';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgCurrency } from '@/hooks/use-settings';
import { apiClient } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';
import { queryKeys } from '@/lib/query-client';
import type {
  MileageUnit,
  OvertimeRateMethod,
  PayrollSettings,
  UpdatePayrollSettingsPayload,
} from '@/lib/types';
import { PAY_WEEK_DAY_NAMES, describeOvertimeRule, payWeekStartDayName } from './overtime-rule';

const MILEAGE_UNIT_LABEL: Record<MileageUnit, string> = {
  miles: 'per mile',
  kilometers: 'per kilometre',
};

const MILEAGE_UNIT_NAME: Record<MileageUnit, string> = {
  miles: 'Miles',
  kilometers: 'Kilometres',
};

export const SETTINGS_CHANGE_IMPACT = 'This changes pay for everyone on the next run.';
export const SELF_APPROVAL_SETTING_LABEL = 'Managers can approve their own time, expenses and per diems';
export const SELF_APPROVAL_SETTING_HINT =
  "When off, a Manager can't approve their own records; Admins can, with a warning. " +
  'Self-approvals are always marked Self-approved and logged.';
export const OVERTIME_RATE_METHOD_HELP =
  "Job rate (default): overtime is paid at the rate of the job where it was worked. Under US federal rules this needs the worker's agreement in advance. Blended: overtime premium uses the week's average rate — use this if you don't have that agreement.";

// PATCH goes to the detail route (`/settings/{id}/`); the list route only
// serves GET, so the settings row id is required.
function useSavePayrollSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdatePayrollSettingsPayload }) => {
      const response = await apiClient.patch<PayrollSettings>(`/api/v1/payroll/settings/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollSettings.all });
    },
  });
}

interface SettingsFormState {
  overtime_rate_method?: OvertimeRateMethod;
  overtime_multiplier: string;
  overtime_threshold_weekly: string;
  pay_week_start_day: string;
  default_per_diem_rate: string;
  default_mileage_rate: string;
  mileage_unit: MileageUnit;
  managers_can_self_approve: boolean;
}

function toFormState(settings: PayrollSettings): SettingsFormState {
  return {
    overtime_rate_method: settings.overtime_rate_method ?? 'job',
    overtime_multiplier: settings.overtime_multiplier,
    overtime_threshold_weekly: settings.overtime_threshold_weekly,
    pay_week_start_day: String(settings.pay_week_start_day),
    default_per_diem_rate: settings.default_per_diem_rate,
    default_mileage_rate: settings.default_mileage_rate,
    mileage_unit: settings.mileage_unit,
    managers_can_self_approve: settings.managers_can_self_approve,
  };
}

const numberChanged = (before: string, after: string) => Number(before) !== Number(after);

/**
 * Only the fields the user actually changed, so a PATCH never overwrites a
 * setting someone else saved while this form was open.
 */
export function toPayload(settings: PayrollSettings, form: SettingsFormState): UpdatePayrollSettingsPayload {
  const payload: UpdatePayrollSettingsPayload = {};
  if ((settings.overtime_rate_method ?? 'job') !== (form.overtime_rate_method ?? 'job')) {
    payload.overtime_rate_method = form.overtime_rate_method ?? 'job';
  }
  if (numberChanged(settings.overtime_multiplier, form.overtime_multiplier)) {
    payload.overtime_multiplier = form.overtime_multiplier;
  }
  if (numberChanged(settings.overtime_threshold_weekly, form.overtime_threshold_weekly)) {
    payload.overtime_threshold_weekly = form.overtime_threshold_weekly;
  }
  if (settings.pay_week_start_day !== Number(form.pay_week_start_day)) {
    payload.pay_week_start_day = Number(form.pay_week_start_day);
  }
  if (numberChanged(settings.default_per_diem_rate, form.default_per_diem_rate)) {
    payload.default_per_diem_rate = form.default_per_diem_rate;
  }
  if (numberChanged(settings.default_mileage_rate, form.default_mileage_rate)) {
    payload.default_mileage_rate = form.default_mileage_rate;
  }
  if (settings.mileage_unit !== form.mileage_unit) {
    payload.mileage_unit = form.mileage_unit;
  }
  if (settings.managers_can_self_approve !== form.managers_can_self_approve) {
    payload.managers_can_self_approve = form.managers_can_self_approve;
  }
  return payload;
}

export interface SettingsChange {
  label: string;
  from: string;
  to: string;
  unit?: string;
}

function trimNumber(value: string): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : value;
}

/**
 * The settings the form would change, as "label: old → new unit" rows for
 * the confirmation dialog. Numeric fields compare by value so "1.50" → "1.5"
 * is not a change.
 */
export function describeSettingsChanges(
  settings: PayrollSettings,
  form: SettingsFormState,
  currency: string,
): SettingsChange[] {
  const changes: SettingsChange[] = [];

  const beforeMethod = settings.overtime_rate_method ?? 'job';
  const afterMethod = form.overtime_rate_method ?? 'job';
  if (beforeMethod !== afterMethod) {
    changes.push({
      label: 'Overtime rate method',
      from: beforeMethod === 'job' ? 'Job rate' : 'Blended',
      to: afterMethod === 'job' ? 'Job rate' : 'Blended',
    });
  }

  if (numberChanged(settings.overtime_multiplier, form.overtime_multiplier)) {
    changes.push({
      label: 'Overtime multiplier',
      from: `${trimNumber(settings.overtime_multiplier)}×`,
      to: `${trimNumber(form.overtime_multiplier)}×`,
    });
  }
  if (numberChanged(settings.overtime_threshold_weekly, form.overtime_threshold_weekly)) {
    changes.push({
      label: 'Weekly hours before overtime',
      from: trimNumber(settings.overtime_threshold_weekly),
      to: trimNumber(form.overtime_threshold_weekly),
      unit: 'hours',
    });
  }
  if (settings.pay_week_start_day !== Number(form.pay_week_start_day)) {
    changes.push({
      label: 'Pay week starts on',
      from: payWeekStartDayName(settings.pay_week_start_day),
      to: payWeekStartDayName(Number(form.pay_week_start_day)),
    });
  }
  if (numberChanged(settings.default_per_diem_rate, form.default_per_diem_rate)) {
    changes.push({
      label: 'Default per diem rate',
      from: formatMoney(settings.default_per_diem_rate, currency),
      to: formatMoney(form.default_per_diem_rate, currency),
      unit: 'per day',
    });
  }
  const mileageFormat = { minimumFractionDigits: 2, maximumFractionDigits: 4 };
  const mileageUnitChanged = settings.mileage_unit !== form.mileage_unit;
  if (numberChanged(settings.default_mileage_rate, form.default_mileage_rate) || mileageUnitChanged) {
    changes.push({
      label: 'Default mileage rate',
      from: `${formatMoney(settings.default_mileage_rate, currency, mileageFormat)} ${MILEAGE_UNIT_LABEL[settings.mileage_unit]}`,
      to: `${formatMoney(form.default_mileage_rate, currency, mileageFormat)} ${MILEAGE_UNIT_LABEL[form.mileage_unit]}`,
    });
  }
  if (mileageUnitChanged) {
    changes.push({
      label: 'Mileage unit',
      from: MILEAGE_UNIT_NAME[settings.mileage_unit],
      to: MILEAGE_UNIT_NAME[form.mileage_unit],
    });
  }
  if (settings.managers_can_self_approve !== form.managers_can_self_approve) {
    changes.push({
      label: SELF_APPROVAL_SETTING_LABEL,
      from: settings.managers_can_self_approve ? 'On' : 'Off',
      to: form.managers_can_self_approve ? 'On' : 'Off',
    });
  }
  return changes;
}

export function formatSettingsChange(change: SettingsChange): string {
  const text = `${change.label}: ${change.from} → ${change.to}`;
  return change.unit ? `${text} ${change.unit}` : text;
}

function SettingRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-3 border-b last:border-b-0">
      <dt className="text-sm font-medium text-gray-600">{label}</dt>
      <dd className="sm:col-span-2">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
      </dd>
    </div>
  );
}

export function PayrollSettingsPage() {
  const permissions = usePermissions();
  // D5 (owner ruling): payroll Managers and above edit settings, with a
  // confirmation; the API already allows it (payroll:viewsets:admin_or_manager).
  const canEdit = permissions.isAdmin || permissions.hasMinModuleRole('payroll', 'manager');
  const orgCurrency = useOrgCurrency();
  const { data: settings, isLoading, error } = usePayrollSettings();
  const save = useSavePayrollSettings();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<SettingsFormState | null>(null);
  // Snapshot of the settings when Edit was clicked. Changes are diffed against
  // this, not the live query, so a background refetch of someone else's save
  // is neither shown as this user's change nor PATCHed back.
  const [baseline, setBaseline] = useState<PayrollSettings | null>(null);
  const [formError, setFormError] = useState('');
  const [pendingChanges, setPendingChanges] = useState<SettingsChange[] | null>(null);

  const startEditing = () => {
    if (!settings) return;
    setBaseline(settings);
    setForm(toFormState(settings));
    setFormError('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setForm(null);
    setBaseline(null);
    setFormError('');
    setPendingChanges(null);
  };

  const update = <K extends keyof SettingsFormState>(key: K, value: SettingsFormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  // Validates, then asks for confirmation; nothing is sent until confirmSave.
  const handleSave = () => {
    if (!settings || !baseline || !form) return;
    setFormError('');
    const numericFields = [
      form.overtime_multiplier,
      form.overtime_threshold_weekly,
      form.default_per_diem_rate,
      form.default_mileage_rate,
    ];
    if (numericFields.some((value) => value.trim() === '')) {
      setFormError('Enter a value for every field; use 0 for a rate that does not apply.');
      return;
    }
    const multiplier = Number(form.overtime_multiplier);
    const threshold = Number(form.overtime_threshold_weekly);
    const perDiem = Number(form.default_per_diem_rate);
    const mileage = Number(form.default_mileage_rate);
    if (!(multiplier >= 1)) {
      setFormError('Overtime multiplier must be 1 or more.');
      return;
    }
    if (!(threshold > 0)) {
      setFormError('Weekly overtime threshold must be greater than 0 hours.');
      return;
    }
    if (!(perDiem >= 0) || !(mileage >= 0)) {
      setFormError('Default rates cannot be negative.');
      return;
    }
    const changes = describeSettingsChanges(baseline, form, orgCurrency);
    if (changes.length === 0) {
      toast.info('No changes to save.');
      cancelEditing();
      return;
    }
    setPendingChanges(changes);
  };

  const confirmSave = async () => {
    if (!settings || !baseline || !form) return;
    try {
      const saved = await save.mutateAsync({ id: settings.id, data: toPayload(baseline, form) });
      if (saved.warnings?.length) {
        toast.warning('Payroll settings saved with a warning.', {
          description: saved.warnings.join(' '),
        });
      } else {
        toast.success('Payroll settings saved.');
      }
      cancelEditing();
    } catch (err) {
      setPendingChanges(null);
      if (isAxiosError(err) && err.response?.status === 403) {
        setFormError("You don't have permission to change payroll settings.");
      } else {
        setFormError('Failed to save payroll settings.');
      }
    }
  };

  const forbidden = isAxiosError(error) && error.response?.status === 403;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payroll Settings</h1>
          <p className="text-gray-600 mt-1">
            The overtime rule, pay week and default rates payroll uses for every worker
          </p>
        </div>
        {canEdit && settings && !editing && (
          <Button onClick={startEditing}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      {!canEdit && (
        <p className="text-sm text-gray-600 bg-gray-50 border rounded-md px-4 py-2">
          Changes are made by a payroll Manager or an organization Admin.
        </p>
      )}

      {settings && !forbidden && settings.warnings?.length > 0 && (
        <div
          role="status"
          className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-4 py-2 space-y-1"
        >
          {settings.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pay policy</CardTitle>
          <CardDescription>
            Workers whose Pay Rate overtime is &ldquo;Auto&rdquo; follow the overtime rule below.
            State overtime rules, where configured, take precedence.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-gray-500">Loading settings...</div>
          ) : forbidden ? (
            <div className="py-8 text-center text-gray-500">
              You don&apos;t have access to payroll settings.
            </div>
          ) : error || !settings ? (
            <div className="py-8 text-center text-gray-500">Couldn&apos;t load payroll settings.</div>
          ) : editing && form ? (
            <div className="space-y-4">
              {formError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{formError}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="overtime-rate-method">Overtime rate method</Label>
                  <Select
                    value={form.overtime_rate_method ?? 'job'}
                    onValueChange={(value) =>
                      update('overtime_rate_method', value as OvertimeRateMethod)
                    }
                  >
                    <SelectTrigger id="overtime-rate-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="job">Job rate (default)</SelectItem>
                      <SelectItem value="blended">Blended</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">{OVERTIME_RATE_METHOD_HELP}</p>
                </div>
                <div>
                  <Label htmlFor="overtime-multiplier">Overtime multiplier</Label>
                  <Input
                    id="overtime-multiplier"
                    type="number"
                    step="0.05"
                    min="1"
                    value={form.overtime_multiplier}
                    onChange={(e) => update('overtime_multiplier', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">1.5 means time and a half.</p>
                </div>
                <div>
                  <Label htmlFor="overtime-threshold">Weekly hours before overtime</Label>
                  <Input
                    id="overtime-threshold"
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.overtime_threshold_weekly}
                    onChange={(e) => update('overtime_threshold_weekly', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="pay-week-start">Pay week starts on</Label>
                  <Select
                    value={form.pay_week_start_day}
                    onValueChange={(v) => update('pay_week_start_day', v)}
                  >
                    <SelectTrigger id="pay-week-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAY_WEEK_DAY_NAMES.map((name, index) => (
                        <SelectItem key={name} value={String(index)}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="default-per-diem">Default per diem rate ({orgCurrency})</Label>
                  <Input
                    id="default-per-diem"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.default_per_diem_rate}
                    onChange={(e) => update('default_per_diem_rate', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used when a worker&apos;s Pay Rate has no per diem rate.
                  </p>
                </div>
                <div>
                  <Label htmlFor="default-mileage">Default mileage rate ({orgCurrency})</Label>
                  <Input
                    id="default-mileage"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.default_mileage_rate}
                    onChange={(e) => update('default_mileage_rate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="mileage-unit">Mileage unit</Label>
                  <Select
                    value={form.mileage_unit}
                    onValueChange={(v) => update('mileage_unit', v as MileageUnit)}
                  >
                    <SelectTrigger id="mileage-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="miles">Miles</SelectItem>
                      <SelectItem value="kilometers">Kilometres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Switch
                  id="managers-can-self-approve"
                  checked={form.managers_can_self_approve}
                  onCheckedChange={(checked) => update('managers_can_self_approve', checked)}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="managers-can-self-approve">{SELF_APPROVAL_SETTING_LABEL}</Label>
                  <p className="text-xs text-gray-500 mt-1">{SELF_APPROVAL_SETTING_HINT}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={save.isPending}>
                  {save.isPending ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={cancelEditing} disabled={save.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <dl>
              <SettingRow
                label="Overtime rate method"
                value={settings.overtime_rate_method === 'blended' ? 'Blended' : 'Job rate'}
                hint={OVERTIME_RATE_METHOD_HELP}
              />
              <SettingRow
                label="Overtime"
                value={`Auto — ${describeOvertimeRule(settings)}`}
                hint="Hours over the weekly threshold are paid at the hourly rate times the multiplier."
              />
              <SettingRow
                label="Pay week starts on"
                value={payWeekStartDayName(settings.pay_week_start_day)}
                hint="Weekly overtime is counted from this day."
              />
              <SettingRow
                label="Default per diem rate"
                value={`${formatMoney(settings.default_per_diem_rate, orgCurrency)} per day`}
                hint="Used when a worker's Pay Rate has no per diem rate."
              />
              <SettingRow
                label="Default mileage rate"
                value={`${formatMoney(settings.default_mileage_rate, orgCurrency, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${MILEAGE_UNIT_LABEL[settings.mileage_unit]}`}
              />
              <SettingRow
                label={SELF_APPROVAL_SETTING_LABEL}
                value={settings.managers_can_self_approve ? 'On' : 'Off'}
                hint={SELF_APPROVAL_SETTING_HINT}
              />
            </dl>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={pendingChanges !== null}
        onOpenChange={(open) => {
          if (!open && !save.isPending) setPendingChanges(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save payroll settings?</AlertDialogTitle>
            <AlertDialogDescription>{SETTINGS_CHANGE_IMPACT}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc pl-5 text-sm text-gray-900 space-y-1">
            {(pendingChanges ?? []).map((change) => (
              <li key={change.label}>{formatSettingsChange(change)}</li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Keep the dialog open while the PATCH is in flight.
                event.preventDefault();
                void confirmSave();
              }}
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving...' : 'Save changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
